import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import {
  type DeliveryNoteReportRequest,
  zodDeliveryNoteReportSchema,
} from './useQueryDeliveryNoteReport';

type ExportReportResponse = {
  requestId: UUID | undefined;
};

const zodDeliveryNoteReportExportSchema = zodDeliveryNoteReportSchema.omit({
  limit: true,
  offset: true,
});

/**
 * Sends a request to initiate exporting a delivery note report in Excel format.
 *
 * @param payload - Parameters for filtering and configuring the report
 * @returns The response for the request_id
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Analytics/operation/request_delivery_note_analytics_excel_analytics_report_export_request_post
 */
export const requestExportReport = async (
  payload: DeliveryNoteReportRequest,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.REQUEST_ANALYTICS_EXPORT(), {
      json: payload,
      validate: {
        body: zodDeliveryNoteReportExportSchema,
      },
    })
    .json<ExportReportResponse>();

  return response;
};

/**
 * React Query based custom hook for requesting export of a delivery note report in Excel format.
 *
 * @returns {UseMutationResult<ExportReportResponse, Error, DeliveryNoteReportRequest>}
 */
export const useMutationRequestDeliveryNoteExcel = () => {
  return useMutation({
    mutationFn: async (payload: DeliveryNoteReportRequest) =>
      toast.promise(requestExportReport(payload), {
        error: 'Fehler beim Laden der Excel-Lieferung!',
        loading: 'Excel-Lieferung wird geladen...',
        success: 'Excel-Lieferung wurde erfolgreich abgeschlossen!',
      }),
  });
};
